// src/components/BoardPack/BoardPackExportHost.jsx
// Rendered once at the App level so it never unmounts on route change.
// Keeps a single BoardPack instance alive in a portal container: while the
// user is on /board-pack that container sits inside the route's visible slot;
// if they navigate away mid-export it's moved off-screen (still attached to
// the document, so html-to-image keeps capturing correctly) instead of being
// unmounted. Once there's no visible slot AND no export running, BoardPack is
// unmounted for real.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useExport } from "@/context/ExportContext";
import BoardPack from "./BoardPack";

const OFFSCREEN_STYLE = {
  position: "fixed",
  top: 0,
  left: "-10000px",
  width: "1280px",
  pointerEvents: "none",
};

const BoardPackExportHost = () => {
  const { isExporting, visibleSlotEl } = useExport();

  const [hostEl] = useState(() => {
    const el = document.createElement("div");
    el.style.width = "100%";
    el.style.height = "100%";
    return el;
  });

  const offscreenRef = useRef(null);
  const shouldRender = Boolean(visibleSlotEl) || isExporting;

  useEffect(() => {
    if (!shouldRender) return;
    const target = visibleSlotEl || offscreenRef.current;
    if (target && hostEl.parentNode !== target) {
      target.appendChild(hostEl);
    }
  });

  return (
    <>
      <div ref={offscreenRef} style={OFFSCREEN_STYLE} aria-hidden="true" />
      {shouldRender && createPortal(<BoardPack />, hostEl)}
    </>
  );
};

export default BoardPackExportHost;
